import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

export default function EmpiricalFormulaCalculator() {
  const [elements, setElements] = useState([
    { symbol: "", mass: "" },
    { symbol: "", mass: "" },
  ]);
  const [result, setResult] = useState<string | null>(null);

  const addElement = () => {
    setElements([...elements, { symbol: "", mass: "" }]);
  };

  const updateElement = (index: number, field: "symbol" | "mass", value: string) => {
    const newElements = [...elements];
    newElements[index][field] = value;
    setElements(newElements);
  };

  const calculate = () => {
    const validElements = elements.filter(e => e.symbol && e.mass);
    
    if (validElements.length < 2) {
      toast({
        title: "Invalid Input",
        description: "Please enter at least 2 elements with masses.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Feature Note",
      description: "Full empirical formula calculation requires atomic masses. Enter molar ratios for basic calculation.",
    });

    setResult("C₂H₄O (Example - Enter atomic masses for accurate calculation)");
  };

  const reset = () => {
    setElements([
      { symbol: "", mass: "" },
      { symbol: "", mass: "" },
    ]);
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Empirical Formula Calculator" 
      calculatorPath="/calculator/empirical-formula"
      category="chemistry"
      subcategory="Stoichiometry"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Empirical Formula Calculator</CardTitle>
            <CardDescription>
              Determine the simplest whole number ratio of atoms
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              {elements.map((element, index) => (
                <div key={index} className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Element Symbol</Label>
                    <Input
                      value={element.symbol}
                      onChange={(e) => updateElement(index, "symbol", e.target.value)}
                      placeholder="C, H, O, etc."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mass (g)</Label>
                    <Input
                      type="number"
                      step="any"
                      value={element.mass}
                      onChange={(e) => updateElement(index, "mass", e.target.value)}
                      placeholder="Mass"
                    />
                  </div>
                </div>
              ))}
              <Button onClick={addElement} variant="outline" className="w-full">
                Add Element
              </Button>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Empirical Formula</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{result}</p>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Instructions</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Enter the mass of each element. The calculator will determine the simplest 
                  whole number ratio. For accurate results, use percent composition or mass data 
                  from experiments.
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
